<template>
    <!-- width는 wrapper에 적용 -->
    <div class="relative" :class="width">
        <div :class="computedBoxClass">
            <input
                :type="currentInputType"
                :placeholder="placeholder"
                :value="inputValue"
                :maxlength="maxlength"
                :readonly="readonly"
                :disabled="disabled"
                :autocomplete="autocomplete"
                :class="computedInputClass"
                class="h-full"
                @input="onInput"
                @blur="$emit('blur', $event)"
                @keydown="restrictToNumberIfPhoneNum"
            />
            <!-- inputtype 이 password 일 때만 show/hide 토글 노출 (기존 ElementsInput 과 동일한 에셋 재사용) -->
            <div
                v-if="inputtype === 'password'"
                class="shrink-0 pr-3 pl-2 flex items-center cursor-pointer"
                @click="togglePasswordVisibility"
            >
                <img
                    v-if="isPasswordVisible"
                    src="@/assets/img/eye_icon_text.svg"
                    alt="Hide Password"
                    class="h-5 w-5"
                />
                <img
                    v-else
                    src="@/assets/img/eye_icon_password.svg"
                    alt="Show Password"
                    class="h-5 w-5"
                />
            </div>
        </div>
        <p v-if="error && typeof error !== 'boolean'" class="mt-1 font-caption-md-regular text-accent-red-3">
            {{ error }}
        </p>
    </div>
</template>

<script>
import { NumberFormatUtil } from '@/plugins/app-util';

export default {
    name: 'DesignInput',
    props: {
        modelValue: { type: [String, Number], default: '' },
        placeholder: { type: String, default: '' },
        inputtype: { type: String, default: 'text' },
        size: {
            type: String,
            default: 'LG',
            validator: (v) => ['MD', 'LG'].includes(String(v).toUpperCase()),
        },
        width: { type: String, default: 'w-full' },
        disabled: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        maxlength: Number,
        error: { type: [String, Boolean], default: '' },
        isPhoneNum: { type: Boolean, default: false },
        numericOnly: { type: Boolean, default: false },
        autocomplete: { type: String, default: undefined },
    },
    emits: ['update:modelValue', 'blur'],
    data() {
        return {
            boxBaseClass: [
                'flex items-center overflow-hidden',
                'bg-background-basic-1 rounded-02',
                'border border-border-basic-2',
                'focus-within:border-primary-3',
                'transition-colors',
            ],
            boxClassMap: {
                LG: 'h-[2.75rem]',
                MD: 'h-[2.25rem]',
            },
            inputBaseClass: [
                'w-full border-none bg-[rgba(0,0,0,0)]',
                'focus:outline-none focus:ring-0',
                'placeholder:text-content-basic-3',
            ],
            inputTextMap: {
                LG: 'font-body-sm-regular',
                MD: 'font-body-sm-regular',
            },
            inputValue: this.isPhoneNum
                ? this.formatInputValue(this.modelValue)
                : String(this.modelValue ?? ''),
            isPasswordVisible: false,
        };
    },
    computed: {
        normalizedSize() {
            return String(this.size).toUpperCase();
        },
        currentInputType() {
            if (this.inputtype === 'password') {
                return this.isPasswordVisible ? 'text' : 'password';
            }
            return this.inputtype || 'text';
        },
        computedBoxClass() {
            const size = this.normalizedSize;
            const sizeClass = this.boxClassMap[size] || '';
            const classes = [...this.boxBaseClass, sizeClass];
            if(this.disabled) {
                classes.push('bg-background-basic-3');
            }
            if(this.readonly) {
                classes.push('bg-background-basic-3');
            }
            if (this.error) {
                classes.push('border-accent-red-3', 'focus-within:border-accent-red-3');
            }
            if (this.disabled) {
                classes.push(
                    'bg-gray-100',
                    'border-gray-200',
                    'cursor-not-allowed'
                );
                return classes
                    .filter(c => !c.includes('focus-within'))
                    .join(' ');
            }

            return classes.join(' ');

        },
        computedInputClass() {
            const size = this.normalizedSize;
            const textClass = this.inputTextMap[size] || '';
            const bgClass = (this.disabled || this.readonly)
                ? 'bg-background-basic-3'
                : 'bg-background-basic-1';
            return [...this.inputBaseClass, textClass, bgClass].join(' ');
        },
    },
    watch: {
        modelValue(val) {
            this.inputValue = this.formatInputValue(val);
        },
    },
    methods: {
        togglePasswordVisibility() {
            if (!this.disabled && !this.readonly) {
                this.isPasswordVisible = !this.isPasswordVisible;
            }
        },
        toPhoneDigits(value) {
            const digits = String(value ?? '').replace(/\D/g, '');

            if (digits.startsWith('63') && digits.length >= 12) {
                return digits.slice(2, 12);
            }

            if (digits.startsWith('09') || digits.startsWith('08')) {
                return digits.slice(1, 11);
            }

            return digits.slice(0, 10);
        },
        formatInputValue(value) {
            if (this.isPhoneNum) {
                return NumberFormatUtil.convertPhoneGlobalToLocal(value) || '';
            }

            return String(value ?? '').trim();
        },
        restrictToNumberIfPhoneNum(event) {
            if ((!this.isPhoneNum && !this.numericOnly) || event.metaKey || event.ctrlKey) return;

            const allowedKeys = [
                'Backspace',
                'Delete',
                'ArrowLeft',
                'ArrowRight',
                'Tab',
                'Enter',
                'Home',
                'End',
            ];

            if (!/^[0-9]$/.test(event.key) && !allowedKeys.includes(event.key)) {
                event.preventDefault();
            }
        },
        onInput(event) {
            if (this.disabled) return;

            if (this.isPhoneNum) {
                const phoneDigits = this.toPhoneDigits(event.target.value);
                const formattedPhoneNumber = this.formatInputValue(phoneDigits);
                this.inputValue = formattedPhoneNumber;
                event.target.value = formattedPhoneNumber;
                this.$emit('update:modelValue', phoneDigits);
                return;
            }

            this.inputValue = String(event.target.value ?? '').trim();
            this.$emit('update:modelValue', this.inputValue);
        },
    },
};
</script>


